import { AnalysisStateSchema, IdSchema, parseContract, type AnalysisState } from '@aibi/contracts';
import { AppError, err, ok, type Result } from '@aibi/shared';
import { z } from 'zod';

/** Version del formato del documento de workspace (independiente de los contratos). */
export const WORKSPACE_DOCUMENT_VERSION = 1;

/** Columnas de la rejilla del canvas; `layout.position` de cada tarjeta se expresa en ellas. */
export const GRID_COLUMNS = 12;
export const DEFAULT_CARD_WIDTH = 6;
export const DEFAULT_CARD_HEIGHT = 4;
export const MIN_CARD_WIDTH = 2;
export const MAX_CARD_WIDTH = GRID_COLUMNS;
export const MIN_CARD_HEIGHT = 1;
export const MAX_CARD_HEIGHT = 16;
/** Altura de una fila de la rejilla del canvas, en rem. */
export const ROW_UNIT_REM = 5;
export const MAX_CARDS = 24;

/** Anchos rapidos del menu (S, M, L, completo). El arrastre admite cualquier entero en el rango. */
export const CARD_WIDTHS = [4, 6, 8, 12] as const;
export type CardWidth = (typeof CARD_WIDTHS)[number];

/** Alturas rapidas del menu (filas de rejilla). */
export const CARD_HEIGHTS = [1, 2, 4, 6, 8] as const;
export type CardHeight = (typeof CARD_HEIGHTS)[number];

export const clampCardWidth = (width: number): number =>
  Math.min(MAX_CARD_WIDTH, Math.max(MIN_CARD_WIDTH, Math.round(width)));

export const clampCardHeight = (height: number): number =>
  Math.min(MAX_CARD_HEIGHT, Math.max(MIN_CARD_HEIGHT, Math.round(height)));

/**
 * Documento de workspace: un conjunto ordenado de tarjetas, cada una un `AnalysisState`
 * completo (intencion analitica + `visualization` + `layout`). Es serializable y no
 * contiene filas, resultados, planes ni estado de UI.
 */
export const WorkspaceDocumentSchema = z.strictObject({
  version: z.literal(WORKSPACE_DOCUMENT_VERSION),
  id: IdSchema,
  name: z.string().min(1).max(200),
  semanticModelId: IdSchema,
  cards: z.array(AnalysisStateSchema).max(MAX_CARDS),
});
export type WorkspaceDocument = z.infer<typeof WorkspaceDocumentSchema>;

export interface CreateWorkspaceOptions {
  readonly id: string;
  readonly name: string;
  readonly semanticModelId: string;
  readonly cards?: readonly AnalysisState[];
}

export function createWorkspace(options: CreateWorkspaceOptions): WorkspaceDocument {
  return {
    version: WORKSPACE_DOCUMENT_VERSION,
    id: options.id,
    name: options.name,
    semanticModelId: options.semanticModelId,
    cards: normalizePositions([...(options.cards ?? [])]),
  };
}

/**
 * Serializacion canonica (claves ordenadas): el mismo documento produce siempre el mismo
 * texto, independientemente del orden en que se construyeron o validaron sus objetos.
 */
export function serializeWorkspace(document: WorkspaceDocument): string {
  return JSON.stringify(canonical(document));
}

const canonical = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonical);
  if (value === null || typeof value !== 'object') return value;
  const record = value as Record<string, unknown>;
  return Object.fromEntries(
    Object.keys(record)
      .sort()
      .map((key) => [key, canonical(record[key])]),
  );
};

/** Igualdad estructural independiente del orden de las claves (p. ej. dos AnalysisState). */
export const sameContent = (a: unknown, b: unknown): boolean =>
  JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));

/** Reconstruye y valida un documento serializado (contratos + coherencia de modelo). */
export function parseWorkspace(input: unknown): Result<WorkspaceDocument, AppError> {
  let raw = input;
  if (typeof input === 'string') {
    try {
      raw = JSON.parse(input) as unknown;
    } catch (cause) {
      return err(new AppError('VALIDATION_ERROR', 'El workspace no es JSON valido.', { cause }));
    }
  }
  const parsed = parseContract(WorkspaceDocumentSchema, raw, 'documento de workspace');
  if (!parsed.ok) return parsed;
  const foreign = parsed.value.cards.findIndex(
    (card) => card.semanticModelId !== parsed.value.semanticModelId,
  );
  if (foreign >= 0) {
    return err(
      new AppError('VALIDATION_ERROR', 'Todas las tarjetas deben usar el modelo del workspace.', {
        details: [{ path: `cards.${foreign}.semanticModelId`, message: 'Modelo distinto.' }],
      }),
    );
  }
  return ok(parsed.value);
}

/** Id determinista para una tarjeta nueva: `card-N` con N mayor que cualquier existente. */
export function nextCardId(cards: readonly Pick<AnalysisState, 'id'>[]): string {
  let max = 0;
  for (const card of cards) {
    const match = /^card-(\d+)$/.exec(card.id);
    if (match?.[1] !== undefined) max = Math.max(max, Number(match[1]));
  }
  return `card-${String(max + 1)}`;
}

export const cardWidth = (card: AnalysisState): number =>
  clampCardWidth(card.layout.position?.w ?? DEFAULT_CARD_WIDTH);

export const cardHeight = (card: AnalysisState): number =>
  clampCardHeight(card.layout.position?.h ?? DEFAULT_CARD_HEIGHT);

export interface GridRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export const cardRect = (card: AnalysisState): GridRect => ({
  x: card.layout.position?.x ?? 0,
  y: card.layout.position?.y ?? 0,
  w: cardWidth(card),
  h: cardHeight(card),
});

export const clampCardX = (x: number, width: number): number =>
  Math.max(0, Math.min(GRID_COLUMNS - clampCardWidth(width), Math.round(x)));

export const clampCardY = (y: number): number => Math.max(0, Math.round(y));

export function clampGridRect(rect: GridRect): GridRect {
  const w = clampCardWidth(rect.w);
  const h = clampCardHeight(rect.h);
  return { x: clampCardX(rect.x, w), y: clampCardY(rect.y), w, h };
}

export const rectsOverlap = (a: GridRect, b: GridRect): boolean =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

const occupiedBy = (
  cards: readonly AnalysisState[],
  exceptCardId?: string,
): GridRect[] =>
  cards.filter((card) => card.id !== exceptCardId).map(cardRect);

/** True si `rect` no choca con ninguna tarjeta ocupada. */
export function isRectFree(
  occupied: readonly GridRect[],
  rect: GridRect,
): boolean {
  const clamped = clampGridRect(rect);
  if (clamped.x !== rect.x || clamped.y !== rect.y || clamped.w !== rect.w || clamped.h !== rect.h) {
    return false;
  }
  return occupied.every((other) => !rectsOverlap(clamped, other));
}

/**
 * Busca hueco libre en la rejilla (permite espacios vacios a la izquierda).
 * Si hay preferencia, prueba ahi, luego a la derecha, abajo, y por ultimo un barrido.
 */
export function findFreePosition(
  cards: readonly AnalysisState[],
  width: number,
  height: number,
  prefer?: { readonly x: number; readonly y: number },
  exceptCardId?: string,
): GridRect {
  const w = clampCardWidth(width);
  const h = clampCardHeight(height);
  const occupied = occupiedBy(cards, exceptCardId);
  const maxY =
    occupied.reduce((max, rect) => Math.max(max, rect.y + rect.h), 0) + Math.max(h, 4);

  const fits = (x: number, y: number): GridRect | null => {
    const rect = clampGridRect({ x, y, w, h });
    return isRectFree(occupied, rect) ? rect : null;
  };

  if (prefer !== undefined) {
    const preferred = fits(prefer.x, prefer.y);
    if (preferred) return preferred;
    const right = fits(prefer.x + w, prefer.y);
    if (right) return right;
    const below = fits(prefer.x, prefer.y + h);
    if (below) return below;
  }

  for (let y = 0; y <= maxY; y++) {
    for (let x = 0; x <= GRID_COLUMNS - w; x++) {
      const found = fits(x, y);
      if (found) return found;
    }
  }
  return clampGridRect({ x: 0, y: maxY, w, h });
}

/** Posicion libre justo al lado (derecha o abajo) de una tarjeta ancla. */
export function findAdjacentPosition(
  cards: readonly AnalysisState[],
  width: number,
  height: number,
  anchor: GridRect,
): GridRect {
  return findFreePosition(cards, width, height, { x: anchor.x + anchor.w, y: anchor.y });
}

/**
 * Corrige posiciones fuera de rango sin empaquetar: los huecos vacios se conservan.
 */
export function normalizePositions(cards: AnalysisState[]): AnalysisState[] {
  return cards.map((card) => {
    const next = clampGridRect(cardRect(card));
    const current = card.layout.position;
    if (
      current?.x === next.x &&
      current.y === next.y &&
      current.w === next.w &&
      current.h === next.h
    ) {
      return card;
    }
    return { ...card, layout: { ...card.layout, position: next } };
  });
}

/**
 * Empaqueta tarjetas en flujo fila a fila (sin huecos). Solo para migrar documentos
 * antiguos o tests que necesiten un layout denso; el canvas libre usa `normalizePositions`.
 */
export function reflowLayout(cards: AnalysisState[]): AnalysisState[] {
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  return cards.map((card) => {
    const w = cardWidth(card);
    const h = cardHeight(card);
    if (x + w > GRID_COLUMNS) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }
    const position = { x, y, w, h };
    x += w;
    rowHeight = Math.max(rowHeight, h);
    const current = card.layout.position;
    if (
      current?.x === position.x &&
      current.y === position.y &&
      current.w === position.w &&
      current.h === position.h
    ) {
      return card;
    }
    return { ...card, layout: { ...card.layout, position } };
  });
}

export function withPosition(card: AnalysisState, rect: GridRect): AnalysisState {
  const position = clampGridRect(rect);
  const current = card.layout.position;
  if (
    current?.x === position.x &&
    current.y === position.y &&
    current.w === position.w &&
    current.h === position.h
  ) {
    return card;
  }
  return { ...card, layout: { ...card.layout, position } };
}
